import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GameView } from "../game/GameView";
import { WordIntro } from "../game/ui/WordIntro";
import { MissedWordReview } from "../game/ui/MissedWordReview";
import {
  computeBonuses,
  rewardSettingsForLesson,
  type GameConfig,
  type RunOutcome,
} from "../game/types";
import type { WordDTO } from "@shared/types";
import { ResultsScreen } from "../components/ResultsScreen";
import { useLocalProfile } from "../local/useLocalProfile";
import { recordRun } from "../local/profile";
import { applyLearnLanguage } from "../local/languageSwap";
import { getWordPack } from "../data/word-packs";
import { getTeachingLevel } from "../data/teaching-levels";
import { resolveQuizKinds } from "../data/quizStyles";
import { loadGameSettings } from "../local/gameSettings";
import { SEED_LESSONS, getSeedLesson, type SeedLesson } from "../data/seed-lessons";
import { getCustomLesson } from "../local/customLessons";
import { clearSession, setSoloSession, touchSession } from "../local/session";
import type { UnlockItem } from "../local/unlocks";
import { UnlockReveal } from "../components/UnlockReveal";
import { fadeOutMenuMusic, sfx, startMenuMusic, stopMenuMusic } from "../game/audio";
import { SINGLE_COURSE_LENGTH, SINGLE_COURSE_SEED } from "../game/courseConfig";

type Lesson = SeedLesson;

function speedForLesson(orderIndex: number): number {
  const base = 13;
  const step = 0.7;
  const max = 22;
  return Math.min(max, base + Math.max(0, orderIndex - 1) * step);
}

function playerLevel(coins: number): number {
  return 1 + Math.floor(coins / 200);
}

type Phase = "loading" | "intro" | "flyover" | "countdown" | "playing" | "review" | "results";
const START_FLYOVER_MS = 4800;

export function LessonPlay() {
  // Two URL shapes supported:
  //   /play/lesson/:lessonId             — old, custom + legacy seed lessons
  //   /play/pack/:packId/L:levelN        — new, pack × teaching-level
  const { lessonId: rawLessonId, packId, levelN } = useParams<{
    lessonId?: string;
    packId?: string;
    levelN?: string;
  }>();
  const lessonId = rawLessonId
    ?? (packId && levelN ? `pack:${packId}::L${levelN}` : undefined);
  const [search] = useSearchParams();
  const quizStyle = search.get("quiz");
  const nav = useNavigate();
  const { profile, setProfile } = useLocalProfile();
  const [phase, setPhase] = useState<Phase>("loading");
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [words, setWords] = useState<WordDTO[]>([]);
  const [outcome, setOutcome] = useState<RunOutcome | null>(null);
  const [recorded, setRecorded] = useState(false);
  const [countdownLabel, setCountdownLabel] = useState<"3" | "2" | "1" | "GO!" | null>(null);
  const [pendingUnlocks, setPendingUnlocks] = useState<UnlockItem[]>([]);

  // No profile? Punt to character creation.
  useEffect(() => {
    if (!profile) nav("/", { replace: true });
  }, [profile, nav]);

  // Resolve a lesson from either URL shape (seed/custom by id, or
  // dynamic pack × teaching-level).
  useEffect(() => {
    if (!lessonId) return;
    let found: Lesson | undefined;
    if (packId && levelN) {
      const pack = getWordPack(packId);
      const tlevel = getTeachingLevel(Number(levelN));
      if (pack && tlevel) {
        // Build a virtual SeedLesson on the fly: the words come from the
        // pack and the mechanic (course mode + challenge type) comes from
        // the teaching level. ChallengeRouter uses challengeKinds; for
        // specialChallenge (spell-tiles / missing-letter / mixed) we
        // mark it on the lesson so the router can pick the right UI.
        found = {
          id: lessonId,
          title: `${pack.title} • L${tlevel.index} ${tlevel.title}`,
          theme: pack.theme,
          timeOfDay: pack.timeOfDay,
          questionsCount: pack.words.length,
          objectsCount: 8,
          coinsCount: 40,
          isGlobal: true,
          orderIndex: tlevel.index,
          challengeKinds: tlevel.challengeKinds ?? null,
          words: pack.words,
        };
      }
    }
    if (!found) found = getSeedLesson(lessonId) ?? getCustomLesson(lessonId) ?? undefined;
    if (!found) {
      nav("/play", { replace: true });
      return;
    }
    // Remember which lesson the player is in so a refresh / lock / home
    // press brings them back here. Cleared on results / home navigation.
    setSoloSession(found.id);
    setLesson(found);
    const sorted = profile ? sortBySrsLocal(found.words, profile.mastery) : found.words;
    setWords(sorted);
    // Skip the pre-level word-teaching card flipper — it interrupted the
    // run flow. Players still see the words inside the gates as they play.
    setPhase("flyover");
  }, [lessonId, profile?.id, nav]);

  const config = useMemo<GameConfig | null>(() => {
    if (!lesson || words.length === 0 || !profile) return null;
    const rewards = rewardSettingsForLesson(lesson.orderIndex, lesson.coinsCount);
    const settings = loadGameSettings();
    // Level 1 = Fall Guys "Hit Parade" introduction course: a straight ramp
    // with bouncing balls rolling toward the player. Shorter and simpler so
    // first-time learners can get a feel for the controls.
    // Teaching-level lookup. New URL form (/play/pack/.../L#) always maps.
    // Old form (legacy seed lesson id) falls back to orderIndex.
    const tlevel = getTeachingLevel(lesson.orderIndex);
    const courseMode = tlevel?.courseMode ?? "default";
    return {
      lessonId: lesson.id,
      words: applyLearnLanguage(words, profile.learnLanguage),
      theme: lesson.theme,
      timeOfDay: lesson.timeOfDay,
      questionsCount: lesson.words.length, // always 5
      objectsCount: lesson.objectsCount,
      coinsCount: rewards.coinsCount,
      coinPickupValue: rewards.coinPickupValue,
      quizCorrectCoins: rewards.quizCorrectCoins,
      courseLength: 260,
      seed: SINGLE_COURSE_SEED,
      courseMode,
      avatar: profile.avatarConfig,
      challengeKinds: resolveQuizKinds(quizStyle, lesson.challengeKinds),
      upgrades: profile.upgrades,
      runSpeed: speedForLesson(lesson.orderIndex),
      movementMode: settings.movementMode,
      movementSensitivity: settings.movementSensitivity,
      cameraSensitivity: settings.cameraSensitivity,
      controllerDeadzone: settings.controllerDeadzone,
      assistMode: settings.assistMode,
      startPaused: true,
      startCameraFlyover: true,
      // Space level wants floaty physics — lower gravity + full air
      // control so the player can steer between moving platforms mid-jump.
      gravityOverride: courseMode === "space-ship" ? Math.max(8, 16 - 2) : undefined,
      airControl: courseMode === "space-ship" ? 1.0 : undefined,
    };
  }, [lesson, words, profile, quizStyle]);

  const handleEnd = (o: RunOutcome) => {
    setOutcome(o);
    // Always go straight to the results screen on finish/death. The old
    // "missed word review" phase re-quizzed wrong answers but felt like the
    // game was asking new questions after the finish line — confusing for
    // kids and prone to getting stuck if a word can't render in review mode.
    setPhase("results");
  };

  // Persist to local profile when we reach the results screen.
  useEffect(() => {
    if (phase !== "results" || !outcome || !lesson || !profile || recorded) return;
    setRecorded(true);
    const bonus = computeBonuses(outcome);
    const result = recordRun(profile, {
      lessonId: lesson.id,
      score: bonus.totalScore,
      timeMs: outcome.timeMs,
      coinsEarned: bonus.totalCoins,
      hearts: outcome.hearts,
      wordOutcomes: outcome.wordOutcomes,
      finished: outcome.finished,
    });
    if (playerLevel(result.profile.coins) > playerLevel(profile.coins)) sfx.levelUp();
    setProfile(result.profile);
    if (result.newlyUnlocked.length > 0) setPendingUnlocks(result.newlyUnlocked);
  }, [phase, outcome, lesson, profile, recorded, setProfile]);

  const handleReviewDone = (corrections: { wordId: string; correct: boolean }[]) => {
    if (!outcome) return;
    const final = outcome.wordOutcomes.map((w) => {
      if (w.correct) return w;
      const last = [...corrections].reverse().find((c) => c.wordId === w.wordId);
      return last ? { wordId: w.wordId, correct: last.correct } : w;
    });
    setOutcome({ ...outcome, wordOutcomes: final });
    setPhase("results");
  };

  useEffect(() => {
    // Keep the resume marker fresh while the lesson is active.
    if (lesson) touchSession();
  }, [phase, lesson]);

  useEffect(() => {
    if (phase === "intro") {
      startMenuMusic();
      return;
    }
    if (phase === "flyover") {
      setCountdownLabel(null);
      startMenuMusic();
      fadeOutMenuMusic(START_FLYOVER_MS);
      const timer = window.setTimeout(() => setPhase("countdown"), START_FLYOVER_MS);
      return () => window.clearTimeout(timer);
    }
    if (phase === "countdown" || phase === "playing") stopMenuMusic();
    if (phase !== "countdown") {
      setCountdownLabel(null);
      return;
    }
    setCountdownLabel("3");
    const timers = [
      window.setTimeout(() => setCountdownLabel("2"), 1000),
      window.setTimeout(() => setCountdownLabel("1"), 2000),
      window.setTimeout(() => setCountdownLabel("GO!"), 3000),
      window.setTimeout(() => setPhase("playing"), 4000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  if (phase === "loading" || !lesson) return <div className="full-center">Loading…</div>;

  if (phase === "intro") {
    return (
      <div className="lesson-intro-page">
        <div className="lesson-intro-top">
        <Link to="/play" className="lesson-intro-back">
          ← Back
        </Link>
        <div>
          <p>Learn Words</p>
          <h1>{lesson.title}</h1>
        </div>
        </div>
        <WordIntro words={words} onDone={() => setPhase("flyover")} />
      </div>
    );
  }

  if ((phase === "flyover" || phase === "countdown" || phase === "playing") && config) {
    return (
      <GameView
        config={config}
        onEnd={handleEnd}
        paused={phase === "flyover" || phase === "countdown"}
        countdownLabel={countdownLabel}
        // Auto-pause-overlay is only for the mid-game pause; flyover should
        // be silent and let the camera fly. The HUD pause button still
        // works during flyover via GameView's userPaused state, so the
        // player can deliberately open the menu from there if they want.
        showPauseOverlay={phase === "playing"}
      />
    );
  }

  if (phase === "review" && outcome) {
    const missedIds = new Set(
      outcome.wordOutcomes.filter((w) => !w.correct).map((w) => w.wordId),
    );
    const missedWords = words.filter((w) => missedIds.has(w.id));
    return (
      <div className="game-root">
        <div
          className="game-canvas"
          style={{
            background: "radial-gradient(ellipse at top, #1c2541 0%, #0b132b 60%)",
            display: "grid",
            placeItems: "center",
          }}
        />
        <MissedWordReview missed={missedWords} allWords={words} onDone={handleReviewDone} />
      </div>
    );
  }

  if (phase === "results" && outcome && lesson) {
    // "Next level" resolution differs by URL form. For pack URLs, jump to
    // the SAME pack's L+1. For legacy seed lessons, walk SEED_LESSONS list.
    let nextHref: string | null = null;
    if (packId && levelN) {
      const nextLvl = Number(levelN) + 1;
      if (nextLvl >= 1 && nextLvl <= 10) {
        nextHref = `/play/pack/${packId}/level/${nextLvl}`;
      }
    } else {
      const sorted = [...SEED_LESSONS].sort((a, b) => a.orderIndex - b.orderIndex);
      const myIdx = sorted.findIndex((l) => l.id === lesson.id);
      const next = myIdx >= 0 && myIdx < sorted.length - 1 ? sorted[myIdx + 1] : null;
      if (next) nextHref = `/play/lesson/${next.id}`;
    }
    const hasNext = nextHref != null;
    const playAgain = () => {
      setRecorded(false);
      setOutcome(null);
      // Reshuffle the word order so a replay is a fresh exam — the SRS
      // sort would normally re-produce the same ordering (mastery just
      // updated). A Fisher-Yates shuffle keeps the same 5 words in the
      // pack but mixes which one each gate asks first.
      setWords((prev) => {
        const arr = [...prev];
        for (let i = arr.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [arr[i], arr[j]] = [arr[j]!, arr[i]!];
        }
        return arr;
      });
      setPhase("flyover");
    };
    const goNext = () => nextHref && nav(nextHref, { replace: true });
    const goHome = () => { clearSession(); nav("/play"); };
    const goMainMenu = () => { clearSession(); nav("/"); };

    return (
      <>
        <ResultsScreen
          outcome={outcome}
          hasNext={hasNext}
          onPlayAgain={playAgain}
          onNext={goNext}
          onHome={goHome}
          onMainMenu={goMainMenu}
          avatar={profile?.avatarConfig}
          coinsAfter={profile?.coins}
          lifetimeRunsAfter={profile?.lifetimeRuns}
          courseMode={config?.courseMode}
          displayName={profile?.displayName}
        />
        {pendingUnlocks.length > 0 && (
          <UnlockReveal
            items={pendingUnlocks}
            onWear={(item) => {
              if (!profile) return;
              const next = { ...profile, avatarConfig: { ...profile.avatarConfig, ...item.apply } };
              setProfile(next);
            }}
            onDone={() => setPendingUnlocks([])}
          />
        )}
      </>
    );
  }

  return <div className="full-center">…</div>;
}

/** Sort lesson words by SRS priority using locally-stored mastery state. */
function sortBySrsLocal(
  words: WordDTO[],
  mastery: Record<string, { srsLevel: number; srsDueAt: number | null; timesSeen: number }>,
): WordDTO[] {
  const now = Date.now();
  const score = (w: WordDTO): number => {
    const m = mastery[w.id];
    if (!m || m.timesSeen === 0) return 0;
    if (m.srsLevel === 0) return 1;
    if (m.srsDueAt != null && m.srsDueAt <= now) return 2;
    const wait = m.srsDueAt ? Math.max(0, m.srsDueAt - now) : 0;
    return 3 + wait / 3_600_000 + m.srsLevel * 0.5;
  };
  return [...words].sort((a, b) => score(a) - score(b));
}
